import type {
  ShipFitting,
  FittingUpgradeTarget,
} from "@frontier/content/ship-fitting";
import { currentShipFitting } from "@frontier/content/ship-fitting";
import { fittingSlot, equippedTier } from "./equipment-selection";
import type { ReactNode } from "react";
import { stationDefinition } from "@frontier/content/stations";
import { useEffect, useState } from "react";
import type { PublicEventsView } from "@frontier/content/events";
import { EventControls } from "./EventControls";
import type { ContractSelection, PassiveId } from "@frontier/content/contracts";
import type { EquipmentKey } from "@frontier/content/arsenal";
import type {
  EquipmentTier,
  EquipmentTiers,
} from "@frontier/content/equipment-tiers";
import type { ArsenalIntent } from "./arsenal-operations";
import { ArsenalPending } from "./ArsenalPending";

import type {
  HullId,
  LoadoutId,
  ModuleId,
  PartyCommand,
  PartyView,
  Profile,
  WeaponId,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import { ContractBoard } from "./ContractBoard";
import { EquipmentSummary } from "./EquipmentSummary";
import { EquipmentCatalog } from "./EquipmentCatalog";
import { GalaxyMapButton } from "./GalaxyMapButton";
import { AccountButton } from "./AccountButton";
import { HelpButton } from "./HelpButton";
import { ChatButton } from "./ChatButton";
import { equipmentSelection } from "./equipment-selection";
import type { EquipmentContext } from "./equipment-selection";
import { PartyControls } from "./PartyControls";
import { RunDebrief } from "./RunDebrief";
import { credits, moduleLabels } from "./ui-labels";

import { tutorialCount, tutorialStage } from "@frontier/content/tutorial";
import { TrainingDockPanel } from "./TrainingDockPanel";
import { HullPicker, nextHullGoal } from "./HullPicker";
import { HullShowcase } from "./HullShowcase";
import { ShipSuppliesDialog } from "./ShipSuppliesDialog";
import { HULLS } from "@frontier/content/definitions";
import "./onboarding.css";
import "./station-dock.css";
import "./station-responsive.css";

import type { UpgradePricing } from "./use-upgrade-prices";
interface Props {
  supplies?: ReactNode;
  season?: ReactNode;
  onShop?: () => void;
  events: PublicEventsView;
  onJoinEvent: (id: string) => void;
  profile: Profile;
  world: WorldSnapshot;
  party: PartyView;
  targetId: string;
  connected: boolean;
  onParty: (command: PartyCommand) => void;
  busy: boolean;
  onLoadout: (
    loadoutId: LoadoutId,
    weaponId?: WeaponId,
    moduleId?: ModuleId,
    passiveId?: PassiveId,
    secondaryModuleId?: ModuleId,
    hullId?: HullId,
    equipmentTiers?: Partial<EquipmentTiers>,
  ) => Promise<void>;
  onHull?: (hullId: HullId) => Promise<void>;
  onFitting?: (fitting: ShipFitting) => Promise<void>;
  onPurchaseHull: (hullId: HullId) => Promise<void>;
  onContracts: (contracts: ContractSelection[]) => Promise<void>;
  onTrainingContract?: () => Promise<void>;
  onCraft: (key: EquipmentKey) => Promise<void>;
  onDismantle: (key: EquipmentKey, tier: EquipmentTier) => Promise<void>;
  onUpgrade: (
    key: EquipmentKey,
    fromTier: EquipmentTier,
    mount?: FittingUpgradeTarget,
  ) => Promise<void>;
  upgradePricing: UpgradePricing;
  pendingArsenal: readonly ArsenalIntent[];
  onRetryArsenal: (intent: ArsenalIntent) => Promise<void>;
  onLaunch: () => void;
  onOpenGalaxy: () => void;
  onAccount: () => void;
  onHelp?: () => void;
  onChat: () => void;
  chatUnread: number;
}
export function Hangar({
  supplies,
  season,
  onShop,
  events,
  onJoinEvent,
  profile,
  world,
  party,
  targetId,
  connected,
  onParty,
  busy,
  onLoadout,
  onPurchaseHull,
  onFitting,
  onHull,
  onContracts,
  onTrainingContract,
  onCraft,
  onDismantle,
  onUpgrade,
  upgradePricing,
  pendingArsenal,
  onRetryArsenal,
  onLaunch,
  onOpenGalaxy,
  onAccount,
  onHelp,
  onChat,
  chatUnread,
}: Props) {
  const [tab, setTab] = useState<"contracts" | "equipment" | "season">(
    "contracts",
  );
  const [suppliesOpen, setSuppliesOpen] = useState(false);
  useEffect(() => setSuppliesOpen(false), [profile.hullId, profile.runId, tab]);
  const [context, setContext] = useState<EquipmentContext>({ kind: "weapon" });
  const [fittingOpen, setFittingOpen] = useState(false);
  const [previewHull, setPreviewHull] = useState<HullId>(profile.hullId);
  useEffect(() => setPreviewHull(profile.hullId), [profile.hullId]);
  const result = profile.lastRunResult;
  const resumeIntro = profile.tutorial !== null && !profile.tutorial.docked;
  const personalTraining = profile.tutorial?.version === 2 && !profile.tutorial.docked;
  const trainingStage = profile.tutorial?.version === 2 && !profile.tutorial.docked ? tutorialStage(profile.tutorial) : null;
  useEffect(() => {
    if (trainingStage === "moduleEquipped") { setTab("equipment"); setFittingOpen(false); }
    if (trainingStage === "contractAccepted") setTab("contracts");
  }, [trainingStage]);
  const firstReturn =
    profile.tutorial?.docked === true &&
    result?.contracts.some((item) => item.contractId === "intro") === true;
  const eventDeparture = world.publicEvents.some(
    (event) =>
      event.id === events.trackedEventId &&
      (event.status === "available" || event.status === "active"),
  );
  const nextHull = nextHullGoal(profile.ownedHulls);
  const inspectedHull =
    resumeIntro && !profile.ownedHulls.includes(previewHull)
      ? profile.hullId
      : previewHull;
  const installHull = (hullId: HullId) =>
    onHull
      ? onHull(hullId)
      : onLoadout(
          profile.loadoutId,
          profile.weaponId,
          profile.moduleId,
          profile.passiveId,
          profile.secondaryModuleId,
          hullId,
          profile.equipmentTiers,
        );
  const chooseEquipment = async (
    key: EquipmentKey | null,
    tier: EquipmentTier = 0,
  ) => {
    const selected = equipmentSelection(profile, context, key, tier);
    if (!selected) return;
    if (selected.shipFitting) {
      await onFitting?.(currentShipFitting(selected.shipFitting));
      return;
    }
    await onLoadout(
      selected.loadoutId,
      selected.weaponId,
      selected.moduleId,
      selected.passiveId,
      selected.secondaryModuleId,
      selected.hullId,
      selected.equipmentTiers,
    );
  };
  return (
    <div className="station-overlay dock-overlay">
      <section
        className={`hangar station-dock ${firstReturn ? "first-return" : ""} ${tab === "equipment" && fittingOpen ? "fitting-mode" : ""}`}
        aria-label="Ангар станции"
        data-training-step={personalTraining ? trainingStage ?? undefined : undefined}
      >
        <header className="dock-header">
          <div className="dock-brand">
            <span>СТАНЦИЯ</span>
            <b>
              {stationDefinition(
                profile.stationId ?? "meridian",
              ).name.toUpperCase()}
            </b>
            <small>ПОЛЁТНАЯ ПАЛУБА</small>
          </div>
          <nav
            className="hangar-tabs dock-tabs"
            role="tablist"
            aria-label="Подготовка к отстыковке"
          >
            <button
              role="tab"
              aria-selected={tab === "contracts"}
              onClick={() => setTab("contracts")}
            >
              КОНТРАКТЫ
            </button>
            <button
              role="tab"
              aria-selected={tab === "equipment"}
              disabled={personalTraining && !profile.tutorial?.rewardGranted}
              onClick={() => setTab("equipment")}
            >
              АНГАР
              {result?.contracts.some(
                (item) => item.moduleUnlock || item.equipmentUnlock !== "none",
              ) && <i />}
            </button>
            <button
              role="tab"
              aria-selected={tab === "season"}
              disabled={personalTraining}
              onClick={() => setTab("season")}
            >
              СЕЗОН
            </button>
          </nav>
          <div className="dock-tools">
            <div className="dock-wallet">
              <button
                className="dock-resource dock-resource-credits"
                onClick={onShop}
                disabled={!onShop}
                aria-label="Пополнить кредиты"
                title="Пополнить кредиты"
              >
                <b>
                  {credits(profile.credits)} <small>КР</small>
                </b>
              </button>
              <button
                className="dock-resource dock-resource-components"
                onClick={onShop}
                disabled={!onShop}
                aria-label="Пополнить компоненты"
                title="Пополнить компоненты"
              >
                <b>
                  {profile.components.toLocaleString("ru-RU")}{" "}
                  <small>КОМП.</small>
                </b>
              </button>
            </div>
            <GalaxyMapButton onOpen={onOpenGalaxy} />
            {onHelp && <HelpButton onOpen={onHelp} />}
            <AccountButton factionId={profile.factionId} onOpen={onAccount} />
            <ChatButton unread={chatUnread} onOpen={onChat} />
            <EventControls
              world={world}
              view={events}
              profile={profile}
              connected={connected}
              onJoin={onJoinEvent}
            />
            <PartyControls
              view={party}
              world={world}
              playerId={profile.playerId}
              targetId={targetId}
              connected={connected}
              onCommand={onParty}
            />
          </div>
          <button
            className="launch-button dock-depart"
            disabled={busy || trainingStage === "contractAccepted" || trainingStage === "moduleEquipped"}
            onClick={onLaunch}
            aria-label="Отстыковаться"
            title={
              profile.selectedContracts.length === 0 &&
              !resumeIntro &&
              !eventDeparture
                ? "Свободный вылет"
                : undefined
            }
          >
            <span>ОТСТЫКОВАТЬСЯ</span>
          </button>
        </header>
        {tab === "season" ? (
          <div className="dock-commerce-scroll">{season}</div>
        ) : tab === "contracts" ? (
          <div className="station-contracts">
            {personalTraining && <TrainingDockPanel profile={profile} busy={busy} onAccept={onTrainingContract} />}
            {firstReturn && profile.selectedContracts.length === 0 &&
              <p className="first-return-hint">Ты на базе своей фракции. Выбери контракты для следующего вылета — теперь решение за тобой.</p>}
            {result && !personalTraining && (
              <details className="dock-report" open={firstReturn && profile.tutorial?.version !== 2}>
                <summary>
                  ПОСЛЕДНЕЕ ВОЗВРАЩЕНИЕ <span>⌄</span>
                </summary>
                <RunDebrief
                  result={result}
                  offers={profile.contractBoard.offers}
                />
                {firstReturn && profile.tutorial?.version !== 2 && (
                  <p className="first-return-hint">
                    «{moduleLabels.volley}» открыт — установи во вкладке «Ангар».{" "}
                    {nextHull && (
                      <span className="hull-goal">
                        Следующий корпус: {nextHull.name} ·{" "}
                        {credits(profile.credits)} / {credits(nextHull.price)}{" "}
                        КР.
                      </span>
                    )}
                  </p>
                )}
              </details>
            )}
            {resumeIntro ? personalTraining ? null : (
              <section className="tutorial-resume contract-board">
                <span className="eyebrow">
                  ПЕРВАЯ ОПЕРАЦИЯ · {tutorialCount(profile.tutorial)}/5
                </span>
                <h2>Вернуться к транспорту</h2>
                <p>
                  {profile.tutorial?.rewardGranted
                    ? "Награда получена. Вернись к транспорту."
                    : "Помоги транспорту и вернись на станцию, чтобы открыть контракты."}
                </p>
              </section>
            ) : (
              <ContractBoard
                offers={profile.contractBoard.offers}
                selected={profile.selectedContracts}
                reputation={profile.reputation}
                busy={busy}
                onChoose={onContracts}
              />
            )}
          </div>
        ) : (
          <div className="hangar-equipment dock-equipment">
            {personalTraining && <TrainingDockPanel profile={profile} busy={busy} onAccept={onTrainingContract} fittingOpen={fittingOpen} />}
            <div
              className={`dock-workshop ${fittingOpen ? "fitting-open" : ""}`}
            >
              {pendingArsenal.length > 0 && (
                <div className="dock-pending">
                  <ArsenalPending
                    pending={pendingArsenal}
                    busy={busy}
                    onRetry={onRetryArsenal}
                  />
                </div>
              )}
              {fittingOpen ? (
                <EquipmentCatalog
                  key={`${context.kind}:${"slot" in context ? (context.slot ?? 0) : 0}`}
                  context={context}
                  profile={profile}
                  busy={busy}
                  progressionAllowed={!resumeIntro}
                  onContext={setContext}
                  onClose={() => setFittingOpen(false)}
                  onSelect={chooseEquipment}
                  onCraft={onCraft}
                  onDismantle={onDismantle}
                  onUpgrade={(key, tier) =>
                    onUpgrade(
                      key,
                      tier,
                      profile.shipFitting &&
                        equippedTier(profile, context, key) === tier
                        ? { hullId: profile.hullId, slot: fittingSlot(context) }
                        : undefined,
                    )
                  }
                  upgradePricing={upgradePricing}
                />
              ) : (
                <>
                  <HullShowcase
                    {...(supplies && inspectedHull === profile.hullId
                      ? { onSupplies: () => setSuppliesOpen(true) }
                      : {})}
                    hullId={inspectedHull}
                    installed={profile.hullId}
                    owned={profile.ownedHulls}
                    balance={profile.credits}
                    components={profile.components}
                    purchasingAllowed={!resumeIntro}
                    busy={busy}
                    onInstall={installHull}
                    onPurchase={onPurchaseHull}
                  />
                  <aside
                    className="dock-module-panel"
                    aria-label="Оборудование корабля"
                  >
                    <h2 className="dock-equipment-title">ОСНАЩЕНИЕ</h2>
                    <EquipmentSummary
                      profile={profile}
                      onOpen={(slot) => {
                        setContext(slot);
                        setFittingOpen(true);
                      }}
                      busy={busy}
                    />
                  </aside>
                </>
              )}
            </div>
            <HullPicker
              selected={profile.hullId}
              previewed={inspectedHull}
              owned={profile.ownedHulls}
              purchasingAllowed={!resumeIntro}
              busy={busy}
              onPreview={setPreviewHull}
            />
          </div>
        )}
        {suppliesOpen && (
          <ShipSuppliesDialog
            shipName={HULLS[profile.hullId].name}
            onClose={() => setSuppliesOpen(false)}
          >
            {supplies}
          </ShipSuppliesDialog>
        )}
      </section>
    </div>
  );
}
